import React, { useState } from "react";
import {
  XAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import MessagesIcon from "../../../assets/icons/messages.svg2";
import { useGetMessagesReportingWidgetDataQuery } from "../../store/slices/widgetsSlice";
import HelpButton from "../../HelpContent/HelpButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faRotate } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { AbsoluteLoadingIndicator } from "../FullScreenLoadingIndicator";

function legendFormatterFactory(totals) {
  return function legendFormatter(value, entry) {
    return (
      <span className="text-white small" style={{ color: entry.color }}>
        {value}: {totals[entry.dataKey]}
      </span>
    );
  };
}

export default function MessagesProcessedwidget() {
  const [period, setPeriod] = useState("last24h");
  const [params, setParams] = useState({
    startDate: "",
    endDate: ",",
    comms: "",
    messageStatus: "",
    timeUnit: "hour",
  });

  const {
    data = { data: [] },
    isLoading,
    isFetching,
    refetch,
  } = useGetMessagesReportingWidgetDataQuery(params);
  const loading = isLoading || isFetching;
  const reportData = data.data.map((i) => ({
    time_period: i.time_period,
    delivered: i.sms.delivered + i.email.delivered + i.voice.delivered,
    sent: i.sms.sent + i.email.sent + i.voice.sent,
    failed: i.sms.failed + i.email.failed + i.voice.failed,
    expired: i.sms.expired + i.email.expired + i.voice.expired,
    pending: i.sms.pending + i.email.pending + i.voice.pending,
    cancelled: i.sms.cancelled + i.email.cancelled + i.voice.cancelled,
  }));

  const totals = reportData.reduce(
    (acc, curr) => {
      acc.delivered += curr.delivered || 0;
      acc.sent += curr.sent || 0;
      acc.failed += curr.failed || 0;
      acc.expired += curr.expired || 0;
      acc.pending += curr.pending || 0;
      acc.cancelled += curr.cancelled || 0;
      return acc;
    },
    {
      delivered: 0,
      sent: 0,
      failed: 0,
      expired: 0,
      pending: 0,
      cancelled: 0,
    }
  );

  return (
    <div className="h-100 rounded-3 position-relative">
      <AbsoluteLoadingIndicator loading={loading} />
      <div className="card-body p-3 d-flex flex-column justify-content-between h-100">
        <div className="text-white">
          <MessagesIcon width={20} className=" white opacity-50" />
          <Link className="btn p-0 text-white" to="/reporting/realtime">
            <h6 className="small fw-bold m-0 mx-2 "> MESSAGES BY STATUS </h6>
          </Link>
          <button
            className="btn p-0 text-white ms-2"
            onClick={() => refetch()}
            disabled={loading}
            title="Refresh messages by status"
          >
            <FontAwesomeIcon
              icon={faRotate}
              className={`opacity-50 ${loading ? "spin" : ""}`}
            />
          </button>
          <HelpButton
            helpKey="widget-messages-by-statuses"
            iconClassName="white"
          />
          <FontAwesomeIcon
            icon={faGripVertical}
            className="ms-2 opacity-50 widget-header"
          />
          <select
            aria-label="Default select example"
            className="border-0 p-2 py-1 rounded-pill bg-white text-xs fw-bold mx-2 text-purple"
            value={params.messageStatus}
            onChange={(e) => {
              setParams((p) => ({ ...p, messageStatus: e.target.value }));
            }}
          >
            <option value="">SHOW ALL</option>
            <option value="delivered">Delivered</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="expired">Expired</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <hr className="my-2" />
        </div>

        <ResponsiveContainer className="flex-grow-1 flex-shrink-1">
          <BarChart
            width={250}
            height={150}
            data={reportData}
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time_period"
              height={15}
              tick={{ stroke: "white", fontSize: "0.6rem" }}
            />
            <Legend
              verticalAlign="top"
              iconSize={10}
              iconType="line"
              formatter={legendFormatterFactory(totals)}
            />
            {(params.messageStatus === "delivered" ||
              params.messageStatus === "") && (
              <Bar dataKey="delivered" fill="#ff7133" />
            )}
            {(params.messageStatus === "sent" ||
              params.messageStatus === "") && (
              <Bar dataKey="sent" fill="#33ff49" />
            )}
            {(params.messageStatus === "failed" ||
              params.messageStatus === "") && (
              <Bar dataKey="failed" fill="#ffdd33" />
            )}
            {(params.messageStatus === "expired" ||
              params.messageStatus === "") && (
              <Bar dataKey="expired" fill="#" />
            )}
            {(params.messageStatus === "pending" ||
              params.messageStatus === "") && (
              <Bar dataKey="pending" fill="#ff8d33" />
            )}
            {(params.messageStatus === "cancelled" ||
              params.messageStatus === "") && (
              <Bar dataKey="cancelled" fill="#ff33be" />
            )}
          </BarChart>
        </ResponsiveContainer>

        <div className="text-white text-center">
          <hr className="my-2" />
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last24h" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last24h");
              setParams({
                startDate: new Date(
                  new Date().setDate(new Date().getDate() - 1)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "hour",
                comms: params.comms,
                messageStatus: params.messageStatus,
              });
            }}
          >
            last 24 h
          </button>
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last7days" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last7days");
              setParams({
                startDate: new Date(
                  new Date().setDate(new Date().getDate() - 7)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "day",
                comms: params.comms,
                messageStatus: params.messageStatus,
              });
            }}
          >
            last 7 days
          </button>{" "}
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last4weeks" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last4weeks");
              setParams({
                startDate: new Date(
                  new Date().setDate(new Date().getDate() - 28)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "week",
                comms: params.comms,
                messageStatus: params.messageStatus,
              });
            }}
          >
            last 4 weeks
          </button>
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last3months" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last3months");
              setParams({
                startDate: new Date(
                  new Date().setMonth(new Date().getMonth() - 3)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "month",
                comms: params.comms,
                messageStatus: params.messageStatus,
              });
            }}
          >
            last 3 months
          </button>
        </div>
      </div>
    </div>
  );
}
